import React, { useContext, useEffect, useRef, useState } from 'react';
import { store } from 'components/Store';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import EmptyMessage from 'components/Dashboard/components/EmptyMessage';
import { OrderCategories } from './EventsHierarchy';
import { useNavigate, useParams } from 'react-router-dom';
import EventsGrouped from './EventsGroups';
import CalendarItem from './CalendarItem';
import SkeletonCalendarList from './SkeletonCalendarLists';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import SortArray from 'components/Dashboard/utilities/SortArray';
import CalendarPicker, { GoToTodayButton } from './CalendarPicker';
import CalendarListControls from './CalendarListControls';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import useResetReference from 'components/Dashboard/SubNav/useResetReference';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import Loading from 'components/Login/Loading';
import { useResetClientNavigation } from 'components/Dashboard/Navigation/ClientNavigation';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import dayjs from 'dayjs';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import time from 'utils/time';

const CalendarList = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists, activePlan } = state;
  const { contentResults, setContentResults } = useContentResults();
  const { UpcomingEvents } = contentResults;
  const { maxNumberOfTeamsThatExpire } = activePlan ?? {};
  const [isLoading, setIsLoading] = useState(true);

  const params = useParams();
  const navigate = useNavigate();
  const { startdate } = params;
  const date = startdate === undefined ? dayjs().format('YYYY-MM-DD') : startdate;

  const [currentButtonToday, setCurrentButtonToday] = useState(true);
  const [month, setMonth] = useState(startdate === undefined ? new Date() : dayjs(startdate).toDate());
  const { generalApiCall } = useGeneralApiCall();
  const { agencyUser, newAgencyPlan } = useTopicOrClientWord();
  const { resetReference } = useResetReference();

  const { isAuthenticated } = useAuth0();
  const { resetClientState } = useResetClientNavigation();
  const { addMetaTags } = useAddMetaTags();

  const keyPressFunction = (e) => {
    if (e.code === 'ArrowRight') {
      navigate(`/calendar/${dayjs(date).add(1, 'day').format('YYYY-MM-DD')}`);
    } else if (e.code === 'ArrowLeft') {
      navigate(`/calendar/${dayjs(date).subtract(1, 'day').format('YYYY-MM-DD')}`);
    }
  };

  useEffect(() => {
    const source = new AbortController();
    resetClientState();
    searchEvents({ day: date, source });
    document.addEventListener('keydown', keyPressFunction);
    return () => {
      document.removeEventListener('keydown', keyPressFunction);
      source.abort('Calendar canceled by the user.');
    };
  }, [date]);

  const eventsCall = async ({ day, source, futureEvent }) => {
    let filters = futureEvent
      ? [{ field: 'eventStartDate', value: `${dayjs(day).format('YYYY-MM-DD')}`, operator: 'DATE_GTE' }]
      : [
          { field: 'eventStartDate', value: `${dayjs(day).format('YYYY-MM-DD')}`, operator: 'DATE_GTE' },
          { field: 'eventStartDate', value: `${dayjs(day).add(1, 'day').format('YYYY-MM-DD')}`, operator: 'DATE_LT' },
        ];
    let method = 'post';
    let pathname = '/api/search/query';
    let requestProperties = {
      type: 'Event',
      query: '',
      pageSize: futureEvent ? 1 : 10000,
      filters,
      sort: { field: 'eventStartDateTimestamp', order: 'ASC' },
      pageNumber: 1,
    };
    let result = await generalApiCall({
      method,
      pathname,
      requestProperties,
      needsAuthentication: true,
      requestSource: source,
      notShowErrorMessage: true,
    });
    return result;
  };

  const searchEvents = async ({ day, source }) => {
    try {
      setIsLoading(true);
      resetReference();
      let eventsRes = await eventsCall({ day, source });
      let lookUpForFutureEvent = false;
      if (eventsRes?.hits?.length === 0 && startdate === undefined) {
        eventsRes = await eventsCall({ day, source, futureEvent: true });
        if (eventsRes?.hits?.length === 1) {
          const dateResult = eventsRes.hits[0].eventStartDate ?? eventsRes.hits[0].dateTime;
          navigate({ pathname: `/calendar/${time.getCalendarDateKey(dateResult)}` }, { replace: true });
        }
        lookUpForFutureEvent = true;
      }
      let highlightEvents = UpcomingEvents?.highlightEvents;
      let recallHighlightEvents = UpcomingEvents?.monthLoaded !== dayjs(day).get('month') || !highlightEvents;
      let upcomingEventsResults = {
        ...eventsRes,
      };

      if (eventsRes) {
        if (startdate === undefined && !lookUpForFutureEvent) {
          navigate(`/calendar/${dayjs().format('YYYY-MM-DD')}`, { replace: true });
        }
        if (isAuthenticated && recallHighlightEvents) {
          highlightEvents = await compareToHighlightEvents({ day, source });
        }
        if (!!highlightEvents || !isAuthenticated) {
          upcomingEventsResults = {
            ...upcomingEventsResults,
            highlightEvents,
            monthLoaded: dayjs(day).get('month'),
          };
          let resultsForState = {
            ...contentResults,
            UpcomingEvents: upcomingEventsResults,
          };

          let title = `${time(time.getCalendarDateKey(date)).formatWeekdayShortDate()} - Calendar - PolicyMogul`;
          let hash = '';
          let contentUrl = `/calendar/${dayjs(date).format('YYYY-MM-DD')}`;
          addMetaTags({ title, hash, contentUrl });
          setContentResults(resultsForState);
          setIsLoading(false);
        }
      }
    } catch (error) {}
  };

  const compareToHighlightEvents = async ({ day, source }) => {
    let results = {};
    if (keywordsLists?.length > 0) {
      const callEvents = await individualApiCallToHighlight({
        id: keywordsLists?.length > 0 ? null : keywordsLists[0].id,
        day,
        source,
      });
      if (callEvents) {
        results = callEvents;
      }
    }
    return results;
  };

  const individualApiCallToHighlight = async ({ id, day, source }) => {
    try {
      let pathname = `/api/search/query-by-keyword-list-v2`;
      let method = 'post';
      let requestProperties = {
        type: 'Event',
        pageSize: 10000,
        filters: [
          {
            field: 'eventStartDate',
            value: `${dayjs(day).startOf('month').format('YYYY-MM-DD')}`,
            operator: 'DATE_GTE',
          },
          {
            field: 'eventStartDate',
            value: `${dayjs(day).endOf('month').add(1, 'day').format('YYYY-MM-DD')}`,
            operator: 'DATE_LT',
          },
        ],
        pageNumber: 1,
        keywordListId: id,
        includeExplanations: false,
        unseenContentOnly: false,
      };
      let results = await generalApiCall({
        pathname,
        method,
        requestProperties,
        requestSource: source,
        needsAuthentication: true,
        notShowErrorMessage: true,
      });
      return results;
    } catch (error) {}
  };

  const arrayElements = UpcomingEvents === undefined ? [] : UpcomingEvents.hits;
  const eventsToShow = EventsGrouped(arrayElements);
  const categoriesNames = Object.keys(eventsToShow);
  const categoriesToShow = OrderCategories(categoriesNames);
  const [commonCategoriesToShow, lordsCategoriesToShow, announcementsCategoriesToShow] = categoriesToShow;
  const [heightContainer, containerRef] = useHeightContainer();

  const isMobile = useIsMobile();
  const scrollbarRef = useRef(null);

  useEffect(() => {
    const eventIdParam = getUrlParam('eventId');
    if (!isLoading && !!eventIdParam && !!scrollbarRef?.current) {
      const eventElement = document.querySelector(`#event-${eventIdParam}`);
      if (!!eventElement) {
        scrollbarRef?.current?.scrollTop(eventElement?.getBoundingClientRect()?.top);
      }
    }
  }, [isLoading, scrollbarRef?.current]);

  return (
    <>
      {isMobile && (
        <div className={`bg-white monitor-items-bar research-bar`}>
          <div className='mx-auto main-content-wrapper'>
            <div className='main-content pr-xl-5 px-md-4 px-3'>
              <CalendarListControls />
            </div>
          </div>
        </div>
      )}
      <div className='bg-main-white monitor-items-bar'>
        <div className='flex-centered main-content-wrapper mx-auto'>
          <div className='side-left-element d-none d-lg-block pl-lg-5 pl-3'>
            <p className='mb-0 title-h4 nowrap-item'>Calendar</p>
          </div>

          <div
            className={`main-content pr-xl-5 px-md-4 px-3 flex-centered justify-content-between calendar-main-title d-none d-lg-flex`}
          >
            <div>
              <CalendarListControls />{' '}
            </div>
            <p className='paragraph-p2 mb-0 specific-y-padding-4 specific-x-padding-15 light-75-text'>
              {maxNumberOfTeamsThatExpire > 0
                ? `Events relevant to your clients and prospects will be highlighted in yellow`
                : `Events coinciding with your ${
                    agencyUser || newAgencyPlan ? `client's` : ''
                  } interests will be highlighted in yellow`}
            </p>
          </div>
        </div>
      </div>
      <div className='pb-lg-0 flex-grow-1 d-flex flex-column mx-auto main-content-wrapper'>
        <div className='d-flex'>
          <div className='side-left-element d-none d-lg-block calendar-reference-filters d-none d-lg-block pt-lg-5 pt-3 pl-lg-5 pl-3'>
            <CalendarPicker month={month} setMonth={setMonth} setCurrentButtonToday={setCurrentButtonToday} />
            <GoToTodayButton
              currentButtonToday={currentButtonToday}
              setCurrentButtonToday={setCurrentButtonToday}
              setMonth={setMonth}
            />
          </div>
          <div className='pr-xl-5 px-md-4 px-3 main-content pt-lg-5 pt-2'>
            {isLoading && <SkeletonCalendarList />}
            {!isLoading && (
              <div ref={containerRef} className=' calendar-page-container pb-0'>
                {arrayElements.length === 0 ? (
                  <EmptyMessage
                    emptyMessageResourceName={'events'}
                    notIncludeButton
                    additionalText='Try selecting another date'
                  />
                ) : (
                  <CustomScrollbar
                    className={'main-content-scrollbar top-content-scrollbar'}
                    style={{ height: `${heightContainer}px` }}
                    maximalThumbYSize={100}
                    ref={scrollbarRef}
                  >
                    <div className='p-5'>
                      {arrayElements.length !== 0 && (
                        <>
                          <div className='position-relative' style={{ zIndex: 3 }}>
                            <RenderItem
                              content={commonCategoriesToShow}
                              mainCategory={'commons'}
                              eventsToShow={eventsToShow}
                            />
                          </div>
                          <div className='position-relative' style={{ zIndex: 2 }}>
                            <RenderItem
                              content={lordsCategoriesToShow}
                              mainCategory={'lords'}
                              eventsToShow={eventsToShow}
                            />
                          </div>
                          <div className='position-relative' style={{ zIndex: 1 }}>
                            <RenderItem
                              content={announcementsCategoriesToShow}
                              mainCategory={'announcements'}
                              eventsToShow={eventsToShow}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </CustomScrollbar>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const RenderItem = (props) => {
  const { contentResults } = useContentResults();
  const { UpcomingEvents } = contentResults;

  const { content, mainCategory, eventsToShow } = props;

  const checkIfEventIsHighlighted = (event) => {
    let highlightEvents = UpcomingEvents?.highlightEvents;
    let highlightedLists = [];
    if (highlightEvents) {
      const { hits, feedItemFlags } = highlightEvents;
      const itemPosition = hits?.findIndex((hit) => hit.id === event.id);
      if (itemPosition >= 0) {
        highlightedLists = feedItemFlags[itemPosition]?.matchingKeywordLists;
      }
    }
    return highlightedLists;
  };

  return (
    <>
      {content.length > 0 && (
        <>
          {content.map((item, index, array) => {
            let eventsToShowOrdered = eventsToShow[item].sort(SortArray('dateTime'));
            return (
              <div className='pb-4 position-relative' key={`${mainCategory}-${index}`} style={{ zIndex: array.length }}>
                <h4 className={`calendar-category calendar-category-${mainCategory}`} key={`category${index}`}>
                  {item}
                </h4>
                {eventsToShowOrdered.map((event, index, arr) => (
                  <CalendarItem
                    key={`event${index}`}
                    item={event}
                    array={arr}
                    index={index}
                    highlightedLists={checkIfEventIsHighlighted(event)}
                  />
                ))}
              </div>
            );
          })}
        </>
      )}
    </>
  );
};

export default withAuthenticationRequired(CalendarList, {
  onRedirecting: () => <Loading />,
});
